/**
 * useRouteTracker — GA4 page_view tracker for Wouter-based routing.
 *
 * How it works:
 *   1. `useLocation()` from Wouter gives us the current pathname; any change
 *      triggers a re-run of the effect below.
 *   2. We schedule the GA4 call via `requestAnimationFrame` so it runs AFTER
 *      the browser has painted the new page — by then react-helmet-async has
 *      committed the updated <title> to the DOM.
 *   3. We read `document.title` and `window.location.href` at call time and
 *      pass them explicitly so GA4 never sees "(not set)".
 *   4. We skip duplicate fires: if the location + title haven't changed since
 *      the last event we don't send again (handles Strict Mode double-invoke
 *      and Suspense re-mounts).
 *
 * Mount inside <WouterRouter> so `useLocation` has access to the router context.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { analytics } from "@/lib/analytics";

export function useRouteTracker() {
  const [location] = useLocation();
  // Track the last pathname+title we fired for so we de-dup.
  const lastFiredRef = useRef<string>("");

  useEffect(() => {
    // rAF defers until after the browser paint, which is after Helmet has
    // written the new <title> to the DOM.
    const raf = requestAnimationFrame(() => {
      const title = document.title;
      const href = window.location.href;
      const key = `${href}::${title}`;

      if (key === lastFiredRef.current) return; // de-dup
      lastFiredRef.current = key;

      analytics.trackPageView(title, href);
    });

    return () => cancelAnimationFrame(raf);
  }, [location]);
}
